
import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer } from '@/components/ui/chart';
import { useIsMobile } from '@/hooks/use-mobile';

interface WeeklyHoursChartProps {
  weeklyHoursData: { name: string; hours: number }[];
  hasTimeEntries: boolean;
}

export function WeeklyHoursChart({ weeklyHoursData, hasTimeEntries }: WeeklyHoursChartProps) {
  const isMobile = useIsMobile();
  
  // Custom tooltip component for charts
  const renderCustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="rounded-lg border bg-background p-2 shadow-sm">
        <div className="font-bold">{payload[0].payload.name}</div>
        <div>{parseFloat(payload[0].value).toFixed(1)} hours</div>
      </div>
    );
  };

  // For mobile, we can simplify data labels
  const formatDayLabel = (day: string) => {
    return isMobile ? day.substring(0, 1) : day;
  };

  // Process data for mobile display if needed
  const processedData = weeklyHoursData.map(item => ({
    ...item,
    name: formatDayLabel(item.name)
  }));

  return (
    <Card className={isMobile ? "border-0 shadow-sm rounded-lg" : ""}>
      <CardHeader className={isMobile ? "p-3 pb-2" : ""}>
        <CardTitle className={isMobile ? "text-lg" : ""}>Weekly Hours</CardTitle>
        <CardDescription className={isMobile ? "text-xs" : ""}>Your logged hours for the past week</CardDescription>
      </CardHeader>
      <CardContent className={`${isMobile ? "p-2" : ""} h-[300px]`}>
        {hasTimeEntries ? (
          <ChartContainer
            config={{
              hours: {
                label: 'Hours',
                color: '#0088FE',
              },
            }}
          >
            <BarChart 
              data={processedData} 
              margin={isMobile ? 
                { top: 20, right: 10, left: 0, bottom: 5 } : 
                { top: 20, right: 30, left: 0, bottom: 5 }
              }
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="name" 
                fontSize={isMobile ? 10 : 12} 
                tickMargin={isMobile ? 5 : 10} 
              />
              <YAxis 
                fontSize={isMobile ? 10 : 12}
                tickMargin={isMobile ? 5 : 10}
                width={isMobile ? 20 : 30}
              />
              <Tooltip content={renderCustomTooltip} />
              <Bar dataKey="hours" fill="var(--color-hours)" />
            </BarChart>
          </ChartContainer>
        ) : (
          <div className="flex h-full items-center justify-center text-muted-foreground">
            No weekly data available
          </div>
        )}
      </CardContent>
    </Card>
  );
}
